import { Cookies } from 'react-cookie';
import {
  WsError,
  WsErrorCodes,
  WsErrorTypes,
  WarpDriveLoginType,
  AppConstants,
  ClientErrorCode,
} from '@amzn/work-spaces-web-client-core-lib';
import { AuthStepDeterminer } from '../AuthStepDeterminer';
import { BaseStep } from './BaseStep';
import { LoginTypeDetector } from '../utils/LoginTypeDetector';

const cookie = new Cookies();

export class WarpDriveStep extends BaseStep {
  /**
   * Performs the authentication step with enhanced error handling
   * @returns The next authentication step or completion status
   */
  async perform() {
    // Fetch clientStateID from session manager
    const clientStateID = this.sessionManager.get('clientStateID');
    // Determine the login type using the LoginTypeDetector
    const authUrl = this.sessionManager.get('nextAuthMethod')?.Url;
    const loginType = LoginTypeDetector.detectLoginTypeFromUrl(
      authUrl,
      this.logger
    );

    try {
      this.validateCsrfToken(clientStateID, loginType);
      const authResponse = await this.authentication.authenticate(
        this.authRequest.constructWarpDriveAuthRequest()
      );
      return AuthStepDeterminer.getNextStepFromAuthResponse(authResponse);
    } catch (error: unknown) {
      // If it's not a WsError, wrap it with appropriate error message
      if (!(error instanceof WsError)) {
        this.logger.error(
          `Authentication error for ${loginType}: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );

        throw new WsError(
          WsErrorTypes.ERROR_TYPE_AUTHENTICATION,
          WsErrorCodes.ERROR_UNKNOWN,
          loginType === WarpDriveLoginType.UNIFIED_LOGIN
            ? ClientErrorCode.AuthExternalUnififedRedirectUnknownError
            : ClientErrorCode.AuthExternalWDRedirectUnknownError
        );
      }
      throw error;
    }
  }

  /**
   * Validates the CSRF token based on the login type
   * @param clientStateID - The client state ID for Unified login
   * @param loginType - The type of WarpDrive login (WD_LOGIN or UNIFIED_LOGIN)
   * @throws WsError with specific error message if validation fails
   */
  private validateCsrfToken(
    clientStateID?: string,
    loginType?: WarpDriveLoginType
  ) {
    const storedCsrfToken = cookie.get(AppConstants.COOKIE_CSRF_TOKEN);

    // Always clear the CSRF token cookie after validation
    cookie.set(AppConstants.COOKIE_CSRF_TOKEN, '', { path: '/' });

    // Determine if we're using Unified login or WD login and log accordingly
    const isUnifiedLogin =
      loginType === WarpDriveLoginType.UNIFIED_LOGIN && clientStateID;
    this.logger.info(
      `Validating CSRF token for ${isUnifiedLogin ? 'Unified' : 'WD'} login`
    );

    // Use a ternary to set the token based on login type
    const csrfToken = isUnifiedLogin
      ? clientStateID
      : new URLSearchParams(window.location.search).get('state');

    // Validation logic is the same for both cases
    const csrfTokenValid = storedCsrfToken && csrfToken === storedCsrfToken;

    if (!csrfTokenValid) {
      this.logger.error('Received invalid csrf token');

      // Use a specific client error code based on login type
      const clientErrorCode =
        loginType === WarpDriveLoginType.UNIFIED_LOGIN
          ? ClientErrorCode.AuthExternalUnififedRedirectUnknownError
          : ClientErrorCode.AuthExternalWDRedirectUnknownError;

      throw new WsError(
        WsErrorTypes.ERROR_TYPE_AUTHENTICATION,
        WsErrorCodes.ERROR_INVALID_INPUT,
        clientErrorCode
      );
    }
  }
}
